import { Map as ImmutableMap, List, Record, type RecordOf } from 'immutable';
import type { SetOptional, ValueOf } from 'type-fest';

import { getLogger } from '../../utils/core';
import type { QcsSyncResourceStore } from '../core/resourceStoreTypes';
import type PaymentMethodsType from '../fmcore-types/PaymentMethodsTypes';

export const ACCOUNTS_REDUCER_KEY = 'accountsStore';
export type ACCOUNTS_REDUCER_KEY = typeof ACCOUNTS_REDUCER_KEY;

const log = getLogger('flux/accounts/accountsTypes');

export type AccountsStore = QcsSyncResourceStore<AccountProps>;

export type AccountClassification = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'UNKNOWN';

export type AccountType =
  | 'BANK'
  | 'CASH'
  | 'CREDIT'
  | 'LOAN'
  | 'REAL_ESTATE'
  | 'VEHICLE'
  | 'OTHER_ASSET'
  | 'OTHER_LIABILITY'
  | 'INVESTMENT'
  | 'GOAL'
  | 'ACCOUNTS_RECEIVABLE'
  | 'ACCOUNTS_PAYABLE'
  | 'PAYMENT'
  | 'BUSINESS' // TODO: remove - does not exist in QCS
  | 'BILL'
  | 'INSURANCE'
  | 'REWARDS'
  | 'PEER_TO_PEER'
  | 'EQUITY'
  | 'OTHER'
  | 'UNCLASSIFIED'
  | 'UNSUPPORTED'
  | 'UNKNOWN';
export type AccountSubType =
  | 'CHECKING'
  | 'SAVINGS'
  | 'CD'
  | 'DIGITAL_CASH'
  | 'CASH_MANAGEMENT'
  | 'MONEY_MARKET'
  | 'OTHER_BANK'
  | 'CREDIT_CARD'
  | 'LINE_OF_CREDIT'
  | 'OTHER_CREDIT'
  | 'AUTO_LOAN'
  | 'CONSUMER_LOAN'
  | 'CONSTRUCTION_LOAN'
  | 'HOME_EQUITY'
  | 'MILITARY_LOAN'
  | 'MORTGAGE'
  | 'SMB' // TODO: undocumented, not sure what this is
  | 'STUDENT_LOAN'
  | 'LOAN'
  | 'OTHER_ASSET'
  | 'BROKERAGE'
  | 'IRA'
  | 'KEOGH'
  | 'ROTH_IRA'
  | 'ROTH_401K'
  | 'SEP_IRA'
  | 'SIMPLE_IRA'
  | 'UGMA_UTMA'
  | '401K'
  | '403B'
  | '529'
  | 'OTHER_INVESTMENTS'
  | 'ACCOUNTS_RECEIVABLE'
  | 'ACCOUNTS_PAYABLE'
  | 'PAYMENT'
  | 'PREPAID_EXPENSES'
  | 'RENTS_HELD_IN_TRUST'
  | 'RENTS_HELD_IN_TRUST_LIABILITY'
  | 'SALES_TAX'
  | 'TRUST'
  | 'TRUST_LIABILITY'
  | 'UNEARNED_INCOME'
  | 'UNDEPOSITED_FUNDS'
  | 'LIABILITY'
  | 'RECURRING_DEPOSIT'
  | 'OVERDRAFT'
  | 'BROKERAGE_NOT_TAXABLE'
  | 'COMMERCIAL_LOAN'
  | 'SMALL_BUSINESS_LOAN'
  | 'OTHER_LOAN'
  | 'ANNUITY'
  | 'WHOLE_LIFE'
  | 'REWARDS'
  | 'EMAIL'
  | 'BILLER'
  | 'TERM_INSURANCE'
  | 'LENDING_LOAN'
  | 'OTHER'
  | 'OWNER_CONTRIBUTIONS'
  | 'OWNER_DISTRIBUTIONS'
  | 'OTHER_EQUITY'
  | 'DUE_FROM_BUSINESS'
  | 'DUE_TO_BUSINESS'
  | 'DUE_FROM_OWNER'
  | 'DUE_TO_OWNER'
  | 'INVESTMENT_IN_BUSINESS'
  | 'NONE'
  | 'LIFE_INSURANCE'
  | 'PROPERTY_INSURANCE'
  | 'OTHER_INSURANCE'
  | 'UNKNOWN';
export type UsageType = 'PERSONAL' | 'BUSINESS';

export interface AccountRecurringTxn {
  unitType: string;
  value: number;
}

export interface BaseAccountProps {
  type: AccountType;
  id?: string | null;
  shadowId?: string;
  clientId?: string;
  createdAt?: string;

  institutionLoginId?: string;

  name?: string;
  subType?: AccountSubType | null;
  usageType?: UsageType;
  businessId?: string;

  description?: string;
  notes?: string;

  isExcludedFromAccountBar?: boolean;
  isExcludedFromBudgets?: boolean;
  isExcludedFromReports?: boolean;
  isA2ABannerDismissed?: boolean;

  onlineBalance?: number;
  onlineBalanceAt?: string;
  normalizedBalance?: number;

  currency?: string;

  isClosed?: boolean;
  closedOn?: string;

  isConnected?: boolean;
  isDeleted?: boolean;
  isIgnored?: boolean;
  // isNotSynced?: boolean, mapped to isDeleted

  modifiedAt?: string;
  balanceAsOf?: number;
  balanceAsOfOn?: string;
  recurringTxn?: AccountRecurringTxn;

  currentBalanceAsOf?: number;
  currentBalanceAsOfOn?: string;

  goalBalance?: number;

  aggregators?: List<AccountAggregator> | null;

  disconnect?: boolean;

  // this is web redux data only, not shared with QCS (ignored in transformers)
  topDownBalance?: number;

  isBankPendingTxnsExcluded?: boolean;
  fundingAccountId?: string;
  cashBalance?: number;
  accountNumberMasked?: string;

  interestRate?: number;
  maturityDateOn?: unknown;
  minimumNoFeeBalance?: unknown;
  minPayment?: unknown;
  cashInterestRate?: unknown;
  creditLimit?: number;
  rewardsType?: unknown;
  annualFee?: unknown;
  statementDueDate?: unknown;
  statementDueAmount?: unknown;
  monthlyFee?: unknown;
  freeBillPay?: unknown;
  atmFeeReimbursement?: unknown;
  cardType?: unknown;
  line1?: unknown;
  line2?: unknown;
  year?: unknown;
  makeId?: unknown;
  make?: unknown;
  modelId?: unknown;
  model?: unknown;
  trimId?: unknown;
  trim?: unknown;
  mileage?: unknown;
  condition?: unknown;
  autoUpdateMiles?: unknown;
  useCalculatedMilesPerMonth?: unknown;
  calculatedMilesPerMonth?: unknown;
  customMilesPerMonth?: unknown;
  city?: unknown;
  state?: unknown;
  country?: unknown;
  zipCode?: unknown;
  statementMinPayment?: unknown;

  // credit fields:
  statementDueAt?: unknown;
  statementCloseAt?: unknown;
  statementPastDueAmount?: unknown;
  statementLastPaymentAmount?: unknown;
  statementLastPaymentAt?: unknown;
  statementCloseBalance?: unknown;
  statementEndAt?: unknown;
}

export interface RealEstateAccountProps extends BaseAccountProps {
  type: 'REAL_ESTATE';
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zipCode?: string | null;
}
export type RealEstateAccount = RecordOf<RealEstateAccountProps>;

export interface VehicleAccountProps extends BaseAccountProps {
  type: 'VEHICLE';
  year?: number | null;
  makeId?: number | null;
  make?: string | null;
  modelId?: number | null;
  model?: string | null;
  trimId?: number | null;
  trim?: string | null;
  condition?: string | null;
  mileage?: number | null;
  zipCode?: string | null;
  autoUpdateMiles?: boolean | null;
  useCalculatedMilesPerMonth?: boolean | null;
  calculatedMilesPerMonth?: number | null;
  customMilesPerMonth?: number | null;
}
export type VehicleAccount = RecordOf<VehicleAccountProps>;

export type AccountAggregatorProps = {
  id?: string | null;

  channel?: string | null;
  cpId?: string | null;
  cpLoginId?: string | null;

  subType?: AccountSubType | null;

  accountName?: string | null;
  accountNumberMasked?: string | null;

  aggStatus?: string;
  aggStatusCode?: string;
  aggStatusDetail?: string;

  lastRefreshAttemptedAt?: string;
  lastRefreshSuccessfulAt?: string;

  onlineBalance?: string;
  onlineBalanceAt?: string;
  availableBalance?: string;
  normalizeOnlineBalance?: boolean;

  interestRate?: number;
  creditLimit?: number;

  // only valid for payment accounts
  paymentMethods?: List<PaymentMethodsType>;
};
export type AccountAggregator = RecordOf<AccountAggregatorProps>;

export type AccountTypeNodeProps = {
  id: string;
  displayName: string;
  qcsType: AccountType;
  qcsSubType?: AccountSubType;

  selectable: boolean;
  connectable: boolean;

  other: boolean;
  business: boolean;
  children?: List<AccountTypeNode> | null;
};
export type AccountTypeNode = RecordOf<AccountTypeNodeProps>;

// ////////////////////////////////////////////////////////////////////////////////////////////////
// Account - Base Account Properties
// ////////////////////////////////////////////////////////////////////////////////////////////////

// ***** WARNING ***** READ before setting any defaults below
//
// NOTE, if you provide any default account props here, you need to explicitly remove them in
// mkUpdateAccount below, to prevent overwriting these values when someone is trying to update only
// specific fields in an account
// *****
export const defaultAccountProps = {
  id: undefined,
  shadowId: undefined,

  clientId: undefined,
  createdAt: undefined,

  institutionLoginId: undefined,

  name: undefined,
  type: undefined,
  subType: undefined,
  usageType: undefined,
  businessId: undefined,

  description: undefined,
  notes: undefined,

  isExcludedFromAccountBar: undefined,
  isExcludedFromBudgets: undefined,
  isExcludedFromReports: undefined,
  isA2ABannerDismissed: undefined,

  onlineBalance: undefined,
  onlineBalanceAt: undefined,
  normalizedBalance: undefined,

  currency: undefined,

  isClosed: undefined,
  closedOn: undefined,

  isConnected: undefined,
  isDeleted: undefined,
  isIgnored: undefined,

  modifiedAt: undefined,
  balanceAsOf: undefined,
  balanceAsOfOn: undefined,
  currentBalanceAsOf: undefined,
  currentBalanceAsOfOn: undefined,

  goalBalance: undefined,

  aggregators: undefined,

  isBankPendingTxnsExcluded: undefined,
  fundingAccountId: undefined,

  disconnect: undefined,
  accountNumberMasked: undefined,
} as const satisfies SetOptional<BaseAccountProps, 'type'>;

export const mkAccountAggregator = Record<AccountAggregatorProps>({
  id: undefined,

  cpId: undefined,
  cpLoginId: undefined,
  channel: undefined,

  subType: undefined,

  accountName: undefined,
  accountNumberMasked: undefined,

  aggStatus: undefined,
  aggStatusCode: undefined,
  aggStatusDetail: undefined,

  lastRefreshAttemptedAt: undefined,
  lastRefreshSuccessfulAt: undefined,

  onlineBalance: undefined,
  onlineBalanceAt: undefined,
  availableBalance: undefined,
  normalizeOnlineBalance: false,

  interestRate: undefined,
  creditLimit: undefined,

  // only valid for payment accounts
  paymentMethods: undefined,
});

// ================================================================================================
// BankAccount - Bank Account Properties & Types
// ================================================================================================

export interface BankAccountProps extends BaseAccountProps {
  type: 'BANK';
  interestRate?: number;
  maturityDateOn?: string;
  minimumNoFeeBalance?: number;
  monthlyFee?: number;
  freeBillPay?: boolean;
  atmFeeReimbursement?: number;
}
export const mkBankAccount = Record<BankAccountProps>({
  ...defaultAccountProps,
  type: 'BANK',
  subType: undefined,
  interestRate: undefined,
  maturityDateOn: undefined,
  minimumNoFeeBalance: undefined,
  monthlyFee: undefined,
  freeBillPay: undefined,
  atmFeeReimbursement: undefined,
});

export interface CashAccountProps extends BaseAccountProps {
  type: 'CASH';
}
export const mkCashAccount = Record<CashAccountProps>({
  ...defaultAccountProps,
  type: 'CASH',
  subType: undefined,
});

// ================================================================================================
// CreditAccount - Credit Account Properties & Types
// ================================================================================================

export interface CreditAccountProps extends BaseAccountProps {
  type: 'CREDIT';
  cardType?: string;
  interestRate?: number;
  maturityDateOn?: string;
  minPayment?: number;
  cashInterestRate?: number;
  creditLimit?: number;
  rewardsType?: string;
  annualFee?: number;
  statementDueDate?: string;
  statementDueAmount?: number;
  statementMinPayment?: number;

  statementDueAt?: string;
  statementCloseAt?: string;
  statementPastDueAmount?: number;
  statementLastPaymentAmount?: number;
  statementLastPaymentAt?: string;

  statementCloseBalance?: number;
  statementEndAt?: string;
}
export const mkCreditAccount = Record<CreditAccountProps>({
  ...defaultAccountProps,
  type: 'CREDIT',
  subType: undefined,
  cardType: undefined,
  interestRate: undefined,
  maturityDateOn: undefined,
  minPayment: undefined,
  cashInterestRate: undefined,
  creditLimit: undefined,
  rewardsType: undefined,
  annualFee: undefined,
  statementDueDate: undefined,
  statementDueAmount: undefined,
  statementMinPayment: undefined,

  statementDueAt: undefined,
  statementCloseAt: undefined,
  statementPastDueAmount: undefined,
  statementLastPaymentAmount: undefined,
  statementLastPaymentAt: undefined,

  statementCloseBalance: undefined,
  statementEndAt: undefined,
});

// ================================================================================================
// GoalAccount - Goal Account Properties & Types
// ================================================================================================

export interface GoalAccountProps extends BaseAccountProps {
  type: 'GOAL';
}

export const mkGoalAccount = Record<GoalAccountProps>({
  ...defaultAccountProps,
  type: 'GOAL',
  subType: null,
});

// ================================================================================================
// LoanAccount - Loan Account Properties & Types
// ================================================================================================

export interface LoanAccountProps extends BaseAccountProps {
  type: 'LOAN';
  cardType?: string;
  interestRate?: number;
  maturityDateOn?: string;
  minPayment?: number;
  cashInterestRate?: number;
  creditLimit?: number;
  rewardsType?: string;
  annualFee?: number;
  statementDueDate?: string;
  statementDueAmount?: number;
  statementMinPayment?: number;
}

export const mkLoanAccount = Record<LoanAccountProps>({
  ...defaultAccountProps,
  type: 'LOAN',
  subType: undefined,
  cardType: undefined,
  minPayment: undefined,
  cashInterestRate: undefined,
  creditLimit: undefined,
  rewardsType: undefined,
  annualFee: undefined,
  statementDueDate: undefined,
  statementDueAmount: undefined,
  statementMinPayment: undefined,
});

// ================================================================================================
// OtherAssertAccount - Other Asset Properties & Types
// ================================================================================================

export interface OtherAssetAccountProps extends BaseAccountProps {
  type: 'OTHER_ASSET';
}

export const mkOtherAssetAccount = Record<OtherAssetAccountProps>({
  ...defaultAccountProps,
  type: 'OTHER_ASSET',
  subType: undefined,
});

// ================================================================================================
// OtherLiabilityAccount - Other Account Properties & Types
// ================================================================================================

export interface OtherLiabilityAccountProps extends BaseAccountProps {
  type: 'OTHER_LIABILITY';
}
export const mkOtherLiabilityAccount = Record<OtherLiabilityAccountProps>({
  ...defaultAccountProps,
  type: 'OTHER_LIABILITY',
  subType: undefined,
});

// ================================================================================================
// InvestmentAccount - Investment Account Properties & Types
// ================================================================================================

export interface InvestmentAccountProps extends BaseAccountProps {
  type: 'INVESTMENT';
}
export const mkInvestmentAccount = Record<InvestmentAccountProps>({
  ...defaultAccountProps,
  type: 'INVESTMENT',
  subType: undefined,
  usageType: undefined,
  cashBalance: undefined,
});

// ================================================================================================
// RealEstateAccount - Real Estate Properties & Types
// ================================================================================================

export const mkRealEstateAccount = Record<RealEstateAccountProps>({
  ...defaultAccountProps,
  type: 'REAL_ESTATE',
  subType: undefined,
  line1: undefined,
  line2: undefined,
  city: undefined,
  state: undefined,
  country: undefined,
  zipCode: undefined,
});

// ================================================================================================
// VehicleAccount - Vehicle Properties & Types
// ================================================================================================

export const mkVehicleAccount = Record<VehicleAccountProps>({
  ...defaultAccountProps,
  type: 'VEHICLE',
  subType: undefined,
  year: undefined,
  makeId: undefined,
  make: undefined,
  modelId: undefined,
  model: undefined,
  trimId: undefined,
  trim: undefined,
  condition: undefined,
  mileage: undefined,
  zipCode: undefined,
  autoUpdateMiles: undefined,
  useCalculatedMilesPerMonth: undefined,
  calculatedMilesPerMonth: undefined,
  customMilesPerMonth: undefined,
});

// ================================================================================================
// AccountsReceivable - Business only (customer invoices)
// ================================================================================================

export interface AccountsReceivableProps extends BaseAccountProps {
  type: 'ACCOUNTS_RECEIVABLE';
  subType: 'ACCOUNTS_RECEIVABLE';
}
export const mkAccountsReceivable = Record<AccountsReceivableProps>({
  ...defaultAccountProps,
  type: 'ACCOUNTS_RECEIVABLE',
  subType: 'ACCOUNTS_RECEIVABLE',
});

// ================================================================================================
// AccountsPayable - Business only (vendor invoices)
// ================================================================================================

export interface AccountsPayableProps extends BaseAccountProps {
  type: 'ACCOUNTS_PAYABLE';
  subType: 'ACCOUNTS_PAYABLE';
}
export const mkAccountsPayable = Record<AccountsPayableProps>({
  ...defaultAccountProps,
  type: 'ACCOUNTS_PAYABLE',
  subType: 'ACCOUNTS_PAYABLE',
});

// ================================================================================================
// Payment - Business only (vendor invoices)
// ================================================================================================

export interface PaymentAccountProps extends BaseAccountProps {
  type: 'PAYMENT';
  subType: 'PAYMENT';
}
export const mkPaymentAccount = Record<PaymentAccountProps>({
  ...defaultAccountProps,
  type: 'PAYMENT',
  subType: 'PAYMENT',
});

// ================================================================================================
// BusinessAccount - Generic Business Account
// ================================================================================================

export interface BusinessAccountProps extends BaseAccountProps {
  type: 'BUSINESS';
}

export const mkBusinessAccount = Record<BusinessAccountProps>({
  ...defaultAccountProps,
  type: 'BUSINESS',
});

// ================================================================================================
// UnknownAccount - Unknown Account Properties & Types
// ================================================================================================

export interface UnknownAccountProps extends BaseAccountProps {
  type: 'UNKNOWN';
  subType: undefined | null;
}
export const mkUnknownAccount = Record<UnknownAccountProps>({
  ...defaultAccountProps,
  type: 'UNKNOWN',
  subType: undefined,
});

// ================================================================================================
// AccountRecordTypes - Union of All Account Record Types
// ================================================================================================

interface TypePropsMap {
  ACCOUNTS_RECEIVABLE: AccountsReceivableProps;
  ACCOUNTS_PAYABLE: AccountsPayableProps;
  CASH: CashAccountProps;
  BANK: BankAccountProps;
  CREDIT: CreditAccountProps;
  GOAL: GoalAccountProps;
  INVESTMENT: InvestmentAccountProps;
  LOAN: LoanAccountProps;
  OTHER_ASSET: OtherAssetAccountProps;
  OTHER_LIABILITY: OtherLiabilityAccountProps;
  PAYMENT: PaymentAccountProps;
  REAL_ESTATE: RealEstateAccountProps;
  VEHICLE: VehicleAccountProps;
  UNKNOWN: UnknownAccountProps;
  BUSINESS: BusinessAccountProps;
}

export type AccountProps = ValueOf<TypePropsMap>;
export type Account = RecordOf<AccountProps>;

export type NonGoalAccountType = Exclude<AccountType, 'GOAL'>;
export type NonGoalAccountProps = Exclude<AccountProps, GoalAccountProps>;
export type NonGoalAccount = RecordOf<NonGoalAccountProps>;

type AccountPropsForType<T extends AccountType> = T extends keyof TypePropsMap ? TypePropsMap[T] : BaseAccountProps;

export const mkAccount = <T extends AccountType>(
  type: T,
  options?: Partial<AccountPropsForType<T> | BaseAccountProps>,
): Account => {
  switch (type) {
    case 'ACCOUNTS_RECEIVABLE':
      return mkAccountsReceivable(options as AccountsReceivableProps);
    case 'ACCOUNTS_PAYABLE':
      return mkAccountsPayable(options as AccountsPayableProps);
    case 'CASH':
      return mkCashAccount(options as CashAccountProps);
    case 'BANK':
      return mkBankAccount(options as BankAccountProps);
    case 'CREDIT':
      return mkCreditAccount(options as CreditAccountProps);
    case 'GOAL':
      return mkGoalAccount(options as GoalAccountProps);
    case 'INVESTMENT':
      return mkInvestmentAccount(options as InvestmentAccountProps);
    case 'LOAN':
      return mkLoanAccount(options as LoanAccountProps);
    case 'OTHER_ASSET':
      return mkOtherAssetAccount(options as OtherAssetAccountProps);
    case 'OTHER_LIABILITY':
      return mkOtherLiabilityAccount(options as OtherLiabilityAccountProps);
    case 'PAYMENT':
      return mkPaymentAccount(options as PaymentAccountProps);
    case 'REAL_ESTATE':
      return mkRealEstateAccount(options as RealEstateAccountProps);
    case 'VEHICLE':
      return mkVehicleAccount(options as VehicleAccountProps);
    case 'BUSINESS':
      return mkBusinessAccount(options as BusinessAccountProps);
    case 'UNKNOWN':
      return mkUnknownAccount(options as UnknownAccountProps);
    default:
      log.warn('Was a new Account Type Added? Need to update mkAccountForType');
      return mkBankAccount(options as BankAccountProps);
  }
};

// ================================================================================================
// Call to get an empty account "template" to make account updates with (based on type)
// ensure all fields are null as to not accidently overwrite anything
// ================================================================================================
export const mkUpdateAccount = <T extends AccountType>(
  type: T,
  options: Partial<AccountPropsForType<T> | BaseAccountProps>,
) =>
  mkAccount<T>(type, {
    currency: undefined,
    id: undefined,
    subType: undefined,
    ...options,
  });

// ================================================================================================
// AccountTypes
// ================================================================================================

export const mkAccountTypeNode = Record<AccountTypeNodeProps>({
  id: 'BANKING',
  displayName: 'BANKING',

  qcsType: 'BANK',
  qcsSubType: undefined,

  selectable: true,
  connectable: true,

  other: false,
  business: false,

  children: undefined,
});

// biome-ignore format: this array should not be formatted
const AccountTypeData = [
  // PARENT_ID,          ID,                                DISPLAY_NAME,                    QCS_TYPE,               QCS_SUBTYPE,                      SELECTABLE, CONNECTABLE, OTHER,  BUSINESS
  ['0',                  'BANK',                           'Banking',                        'BANK',                 undefined,                        true,       true,        false,  false],
  ['BANK',               'CHECKING',                       'Checking',                       'BANK',                 'CHECKING',                       true,       true,        false,  false],
  ['BANK',               'SAVINGS',                        'Savings',                        'BANK',                 'SAVINGS',                        true,       true,        false,  false],
  ['BANK',               'CASH',                           'Cash',                           'CASH',                 undefined,                        true,       false,       false,  false],
  ['BANK',               'CASH_MANAGEMENT',                'Cash Management',                'BANK',                 'CASH_MANAGEMENT',                true,       true,        false,  false],
  ['BANK',               'DIGITAL_CASH',                   'Digital Cash',                   'BANK',                 'DIGITAL_CASH',                   true,       true,        false,  false],
  ['BANK',               'MONEY_MARKET',                   'Money Market',                   'BANK',                 'MONEY_MARKET',                   true,       true,        false,  false],
  ['BANK',               'CD',                             'CD',                             'BANK',                 'CD',                             true,       true,        false,  false],
  ['BANK',               'OTHER_BANKING',                  'Other Banking',                  'BANK',                 'OTHER_BANK',                     true,       true,        true,   false],
  ['BANK',               'BANK_BUSINESS_ONLY',             'Business only',                  'BANK',                 undefined,                        true,       true,        false,  true],
  ['BANK_BUSINESS_ONLY', 'TRUST',                          'Client Trust',                   'BANK',                 'TRUST',                          true,       true,        false,  true],
  ['BANK_BUSINESS_ONLY', 'RENTS_HELD_IN_TRUST',            'Rents Held in Trust',            'BANK',                 'RENTS_HELD_IN_TRUST',            true,       true,        false,  true],
  ['0',                  'CREDIT',                         'Credit',                         'CREDIT',               undefined,                        true,       true,        false,  false],
  ['CREDIT',             'CREDIT_CARD',                    'Credit Card',                    'CREDIT',               'CREDIT_CARD',                    true,       true,        false,  false],
  ['CREDIT',             'LINE_OF_CREDIT',                 'Line of Credit',                 'CREDIT',               'LINE_OF_CREDIT',                 true,       true,        false,  false],
  ['CREDIT',             'OTHER_CREDIT',                   'Other Credit',                   'CREDIT',               'OTHER_CREDIT',                   true,       true,        true,   false],
  ['0',                  'INVESTMENT',                     'Investments',                    'INVESTMENT',           undefined,                        true,       true,        false,  false],
  ['INVESTMENT',         'BROKERAGE',                      'Brokerage',                      'INVESTMENT',           'BROKERAGE',                      true,       true,        false,  false],
  ['INVESTMENT',         '401K',                           '401K',                           'INVESTMENT',           '401K',                           true,       true,        false,  false],
  ['INVESTMENT',         'IRA',                            'IRA',                            'INVESTMENT',           'IRA',                            true,       true,        false,  false],
  ['INVESTMENT',         'KEOGH',                          'Keogh',                          'INVESTMENT',           'KEOGH',                          true,       true,        false,  false],
  ['INVESTMENT',         'ROTH_IRA',                       'Roth IRA',                       'INVESTMENT',           'ROTH_IRA',                       true,       true,        false,  false],
  ['INVESTMENT',         'ROTH_401K',                      'Roth 401K',                      'INVESTMENT',           'ROTH_401K',                      true,       true,        false,  false],
  ['INVESTMENT',         'SEP_IRA',                        'SEP IRA',                        'INVESTMENT',           'SEP_IRA',                        true,       true,        false,  false],
  ['INVESTMENT',         'SIMPLE_IRA',                     'Simple IRA',                     'INVESTMENT',           'SIMPLE_IRA',                     true,       true,        false,  false],
  ['INVESTMENT',         '529_PLAN',                       '529 Plan',                       'INVESTMENT',           '529',                            true,       true,        false,  false],
  ['INVESTMENT',         'UGMA_UTMA',                      'Custodial (UGMA/UTMA)',          'INVESTMENT',           'UGMA_UTMA',                      true,       true,        false,  false],
  ['INVESTMENT',         '403B',                           '403B',                           'INVESTMENT',           '403B',                           true,       true,        false,  false],
  ['INVESTMENT',         'OTHER_INVESTMENT',               'Other Investment',               'INVESTMENT',           'OTHER_INVESTMENTS',              true,       true,        true,   false],
  ['0',                  'ASSET',                          'Asset',                          'OTHER_ASSET',          undefined,                        true,       false,       false,  false],
  ['ASSET',              'REAL_ESTATE',                    'Real Estate',                    'REAL_ESTATE',          undefined,                        true,       false,       false,  false],
  ['ASSET',              'VEHICLE',                        'Vehicle',                        'VEHICLE',              undefined,                        true,       false,       false,  false],
  ['ASSET',              'OTHER_ASSET',                    'Other Asset',                    'OTHER_ASSET',          'OTHER_ASSET',                    true,       false,       true,   false],
  ['ASSET',              'ASSET_BUSINESS_ONLY',            'Business only',                  'OTHER_ASSET',          undefined,                        true,       false,       false,  true],
  ['ASSET_BUSINESS_ONLY','PREPAID_EXPENSES',               'Prepaid Expenses',               'OTHER_ASSET',          'PREPAID_EXPENSES',               true,       false,       false,  true],
  ['0',                  'LOAN',                           'Liability',                      'LOAN',                 undefined,                        true,       true,        false,  false],
  ['LOAN',               'AUTO',                           'Vehicle Loan',                   'LOAN',                 'AUTO_LOAN',                      true,       true,        false,  false],
  ['LOAN',               'MORTGAGE',                       'Mortgage',                       'LOAN',                 'MORTGAGE',                       true,       true,        false,  false],
  ['LOAN',               'STUDENT',                        'Student Loan',                   'LOAN',                 'STUDENT_LOAN',                   true,       true,        false,  false],
  ['LOAN',               'CONSUMER',                       'Consumer Loan',                  'LOAN',                 'CONSUMER_LOAN',                  true,       true,        false,  false],
  ['LOAN',               'HOME_EQUITY',                    'Home Equity Loan',              'LOAN',                 'HOME_EQUITY',                     true,       true,        false,  false],
  ['LOAN',               'CONSTRUCTION',                   'Construction Loan',              'LOAN',                 'CONSTRUCTION_LOAN',              true,       true,        false,  false],
  ['LOAN',               'MILITARY',                       'Military Loan',                  'LOAN',                 'MILITARY_LOAN',                  true,       true,        false,  false],
  ['LOAN',               'OTHER_LOAN',                     'Other Loan',                     'LOAN',                 'LOAN',                           true,       true,        true,   false],
  ['LOAN',               'OTHER_LIABILITY',                'Other Liability',                'OTHER_LIABILITY',      'LIABILITY',                      true,       false,       true,   false],
  ['LOAN',               'LOAN_BUSINESS_ONLY',             'Business only',                  'LOAN',                 undefined,                        true,       true,        false,  true],
  ['LOAN_BUSINESS_ONLY', 'SMALL_BUSINESS',                 'Small Business Loan',            'LOAN',                 'SMB',                            true,       true,        false,  true],
  ['LOAN_BUSINESS_ONLY', 'TRUST_LIABILITY',                'Client Trust Liability',         'OTHER_LIABILITY',      'TRUST_LIABILITY',                true,       false,       false,  true],
  ['LOAN_BUSINESS_ONLY', 'RENTS_HELD_IN_TRUST_LIABILITY',  'Rents Held in Trust Liability',  'OTHER_LIABILITY',      'RENTS_HELD_IN_TRUST_LIABILITY',  true,       false,       false,  true],
  ['LOAN_BUSINESS_ONLY', 'UNEARNED_INCOME',                'Unearned Income',                'OTHER_LIABILITY',      'UNEARNED_INCOME',                true,       false,       false,  true],
  ['0',                  'BUSINESS',                       'Business Payments',              'BUSINESS',             undefined,                        true,       true,        false,  true],
  ['BUSINESS',           'ACCOUNTS_RECEIVABLE',            'Accounts Receivable',            'ACCOUNTS_RECEIVABLE',  'ACCOUNTS_RECEIVABLE',            false,      false,       false,  true],
  ['BUSINESS',           'ACCOUNTS_PAYABLE',               'Accounts Payable',               'ACCOUNTS_PAYABLE',     'ACCOUNTS_PAYABLE',               true,       false,       false,  true],
  ['BUSINESS',           'PAYMENT',                        'Online Payments',                'PAYMENT',              'PAYMENT',                        true,       true,       false,  true],
  ['BUSINESS',           'SALES_TAX',                      'Sales Tax',                      'OTHER_LIABILITY',      'SALES_TAX',                      true,       false,       false,  true],
  ['BUSINESS',           'UNDEPOSITED_FUNDS',              'Undeposited Funds',              'OTHER_ASSET',          'UNDEPOSITED_FUNDS',              true,       false,       false,  true],
  ['0',                  'UNKNOWN',                        'Unknown',                        'UNKNOWN',              'UNKNOWN',                        false,      false,       false,  true]
] as const;

interface AccountTypeDataItem {
  id: string;
  displayName: string;
  qcsType: AccountType;
  qcsSubType: AccountSubType | undefined;
  selectable: boolean;
  connectable: boolean;
  other: boolean;
  business: boolean;
}

const AccountTypeDataById = AccountTypeData.reduce((map, data) => {
  const parentId = data[0] || '0';
  map.set(parentId, map.get(parentId) || []);

  const parent = map.get(parentId);
  if (parent) {
    parent.push({
      id: data[1],
      displayName: data[2],
      qcsType: data[3],
      qcsSubType: data[4],
      selectable: data[5],
      connectable: data[6],
      other: data[7],
      business: data[8],
    });
  }

  return map;
}, new Map<string, Array<AccountTypeDataItem>>());

const createAccountTypeNode = (
  accountTypesByParentId: typeof AccountTypeDataById,
  data: AccountTypeNodeProps,
  children?: Array<AccountTypeDataItem>,
): AccountTypeNode => {
  const childNodes = children
    ? List(
        children.map((child) => {
          const innerChildren = child.id ? accountTypesByParentId.get(child.id) : undefined;
          return createAccountTypeNode(accountTypesByParentId, child, innerChildren);
        }),
      )
    : null;
  return mkAccountTypeNode({
    id: data.id,
    displayName: data.displayName,
    qcsType: data.qcsType,

    qcsSubType: data.qcsSubType,

    selectable: data.selectable,
    connectable: data.connectable,

    other: data.other,

    children: childNodes,
    business: data.business,
  });
};

export const AccountTypeNodesTree = List(
  AccountTypeDataById.get('0')?.map((data) => {
    const children = AccountTypeDataById.get(data.id);
    return createAccountTypeNode(AccountTypeDataById, data, children);
  }),
);

const addAccountTypeNodeToIdMap = (map: ImmutableMap<string, AccountTypeNode>, node: AccountTypeNode) => {
  map.set(node.id, node);
  if (node.children) {
    node.children.forEach((child) => addAccountTypeNodeToIdMap(map, child));
  }
};
const AccountTypeNodesById = ImmutableMap<string, AccountTypeNode>().withMutations((map) => {
  AccountTypeNodesTree.forEach((node) => addAccountTypeNodeToIdMap(map, node));
});

function buildAccountTypeNodesByQcsType(accountTypeNodesById: typeof AccountTypeNodesById) {
  const subTypesByType = new Map<string, Array<[AccountSubType | 'null', AccountTypeNode]>>();
  accountTypeNodesById.forEach((node) => {
    subTypesByType.set(node.qcsType, subTypesByType.get(node.qcsType) || []);

    const subTypes = subTypesByType.get(node.qcsType);
    if (subTypes) {
      subTypes.push([node.qcsSubType ? node.qcsSubType : 'null', node]);
    }
  });

  const nodesByQcsType = ImmutableMap<string, ImmutableMap<string, AccountTypeNode>>().withMutations((map) => {
    subTypesByType.forEach((value, key) => map.set(key, ImmutableMap(value)));
  });

  return nodesByQcsType;
}
const AccountTypeNodesByQcsType = buildAccountTypeNodesByQcsType(AccountTypeNodesById);

export const getAccountTypeNodeByQcsType = (qcsType: AccountType, qcsSubType: AccountSubType | undefined | null) => {
  const subTypesOfNode = AccountTypeNodesByQcsType.get(qcsType);
  if (!subTypesOfNode) {
    log.warn('Unknown account type', qcsType);
    const node = AccountTypeNodesById.get('UNKNOWN');
    if (node == null) {
      throw new Error('Expected to find an account type node for UNKNOWN types');
    }
    return node;
  }
  let node: AccountTypeNode | null | undefined = null;
  if (qcsSubType) {
    // get exact node that matches QCS
    node = subTypesOfNode.get(qcsSubType);
  }
  if (!node) {
    // if we cannot find subtype, then map to the other category belonging to same parent
    node = subTypesOfNode.find((value) => value.other);
  }
  if (!node) {
    // if we still don't have node, try to use parent node (this may have issues with Select menus!)
    node = subTypesOfNode.get('null');
  }
  if (!node) {
    node = AccountTypeNodesById.get('UNKNOWN');
    if (node == null) {
      throw new Error('Expected to find an account type node for UNKNOWN types');
    }
  }
  return node;
};

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [ACCOUNTS_REDUCER_KEY]: AccountsStore;
  }
}
